import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useContext,
} from 'react';
import { useImmerReducer } from 'use-immer';
import { Alert } from '@nextiva/next-ui-components';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { LocationService } from 'services';
import { bulkEmail } from 'services/IAMOS';
import { useRootUserList } from 'customHooks/userCustomHooks';
import { userReactivateBulk, passwordResetBulk } from 'api/userApi';
import { getTeams } from 'api/teamApi';
import { getRoles } from 'api/roleApi';
import { Page } from 'containers';
import Drawer from 'containers/Drawer';
import {
  SearchBarWrapper,
  TableHeader,
  UserList,
  TimedAlert,
} from 'components';
import { SvgActive, SvgInActive } from 'components/SvgIcon';
import { CREATE_USER, EDIT_USER, ADD_TEAM } from 'constants/drawerTitles';
import { DIALOG_KINDS } from 'constants/dialogKinds';
import { toOptions } from 'utils/display-helpers';
import { getLicenseList } from 'api/licenseService';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { VOICE_ADMIN_DISABLE_DUETO_BWPATCHING } from 'constants/messageText';
import { isAdminAndVoiceUser } from 'utils/user-identifier';
import { useGetInfo, useSnackbar } from 'hooks';
import { screens } from 'containers/pages/DataImport';
import ChimeDrawer from './ChimeDrawer';
import UserDrawerContent from './UserDrawerContent';
import { reducer } from './reducer';
import { reducer as chimeReducer } from './ChimeDrawer/reducer';
import { UserContext, InitialState, actionTypes } from './usersContext';
import {
  ChimeContext,
  DispatchChimeContext,
  InitialState as chimeInitialState,
} from './ChimeDrawer/context';
import DeactivateUsersModal from './DeactivateUsersModal';
import CreateUserModal from './CreateUserModal';
import VoiceSetupModel from './VoiceSetupModel';
import { ShadowUserList } from './shadow-dom';
import { formatUsers } from './helpers';

const importDropdownOptions = [
  {
    importName: 'Bulk add new users',
    editImportName: 'Bulk edit existing users',
  },
];
const { getLocations } = LocationService;

const getDrawerTitle = (
  drawerTitle,
  { first_name, last_name, firstName, lastName },
) => {
  if (drawerTitle === EDIT_USER)
    return `${first_name ?? firstName} ${last_name ?? lastName}`;

  return drawerTitle;
};

const ManageUsers = () => {
  const [userState, dispatch] = useImmerReducer(reducer, InitialState);
  const [chimeState, chimeDispatch] = useImmerReducer(
    chimeReducer,
    chimeInitialState,
  );

  const {
    baseUserList,
    userList,
    userListIsLoading,
    editUser,
    selectedUserList,
    teamsOptions,
    rolesOptions,
    locationsOptions,
    userSearchString,
    notifyModal: { open: openCreateUserModel },
    voiceSetupModel: {
      open: openVoiceSetupModel,
      userName,
      pageName: pageNameVoiceSetupModel,
    },
  } = userState;
  const {
    rootState: { rootUserList, rootUserListLoading, rootUserListError },
    updateRootUserList,
  } = useRootUserList();
  const [hasUserListError, setHasUserListError] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState(CREATE_USER);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fireAlert, setFireAlert] = useState(() => () => {});
  const [isVoiceAccount, setVoiceAccount] = useState(false);
  const [primaryIdentifier, setPrimaryIdentifier] = useState(false);
  const setSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { data: currentUser } = useGetInfo();
  const {
    featureState: { authV3Enabled, isClusterIdInBWPatching },
    corpAcctNbr,
  } = useContext(FeatureContext);
  const { userUpdate } = useSelector((state) => state?.users);
  const contextValue = useMemo(
    () => ({
      userState,
      dispatch,
    }),
    [userState, dispatch],
  );

  const showAlert = (flavor, title, message = '') => {
    fireAlert(flavor, title, message);
  };

  const { data: licenses } = useQuery(
    'licenseList',
    () => getLicenseList(authV3Enabled, corpAcctNbr),
    {
      onError: () => {
        showAlert(
          DIALOG_KINDS.ERROR,
          'Error',
          'An error occurred while retrieving licenses.',
        );
      },
    },
  );

  const { data: options } = useQuery(
    'getTeamsOptions',
    () => teamsOptions.length === 0 && getTeams(),
    {
      onError: () =>
        showAlert(
          DIALOG_KINDS.ERROR,
          'Error',
          'An error occurred while retrieving teams.',
        ),
    },
  );

  const { data: payload } = useQuery(
    'getRoles',
    () => rolesOptions.length === 0 && getRoles(),
    {
      onError: () => {
        showAlert(
          DIALOG_KINDS.ERROR,
          'Error',
          'An error occurred while retrieving roles.',
        );
      },
    },
  );

  const { data: locations } = useQuery(
    'locations',
    () => locationsOptions.length === 0 && getLocations(),
    {
      onError: () => {
        showAlert(
          DIALOG_KINDS.ERROR,
          'Error',
          'An error occurred while retrieving location.',
        );
      },
    },
  );

  const { data: account } = useQuery(
    'isAdminAndVoiceUser',
    isAdminAndVoiceUser,
  );

  useEffect(() => {
    if (account) {
      setVoiceAccount(account);
    }
    if (locations) {
      dispatch({
        type: actionTypes.SET_LOCATIONS_OPTIONS,
        locationsOptions: locations
          .map((location) => ({
            label: location.locationName,
            value: location.locationId.toString(),
            key: location.locationId,
            description: location.locationName,
          }))
          .sort((a, b) => {
            return a.label.localeCompare(b.label);
          }),
      });
    }
    if (payload) {
      dispatch({
        type: actionTypes.SET_ROLES_OPTIONS,
        rolesOptions: toOptions(payload.result).sort((a, b) => {
          return a.label.localeCompare(b.label);
        }),
      });
    }
    if (options) {
      dispatch({
        type: actionTypes.SET_TEAMS_OPTIONS,
        teamsOptions: options.map((team) => ({
          label: team.name,
          value: { ...team, id: team.teamId },
          key: team.teamId,
        })),
      });
    }
    if (licenses) {
      dispatch({
        type: actionTypes.SET_LICENSE_LIST,
        licenseList: licenses,
      });
    }
  }, [account, locations, payload, options, licenses]);

  useEffect(() => {
    if (userUpdate) {
      updateRootUserList();
    }
  }, [userUpdate]);

  useEffect(() => {
    (async () => {
      const identifier = authV3Enabled
        ? Boolean(currentUser?.profileDTO?.nextOSIdent?.primaryIdentifier)
        : Boolean(currentUser?.profiles?.idents?.nextOS?.primary_identifier);
      setPrimaryIdentifier(identifier);
    })();
  }, []);

  // Sets userList from rootUserList
  useEffect(() => {
    if (rootUserListError) {
      setHasUserListError(true);
      dispatch({
        type: actionTypes.SET_USER_LIST_IS_LOADING,
        isLoading: false,
      });

      return;
    }
    // only set userList if its done loading and if it hasn't already been set
    if (rootUserListLoading && userList.length === 0) {
      dispatch({
        type: actionTypes.SET_USER_LIST_IS_LOADING,
        isLoading: true,
      });

      return;
    }
    const formattedUsers = formatUsers(
      rootUserList,
      locationsOptions,
      authV3Enabled,
      corpAcctNbr,
    );

    dispatch({
      type: actionTypes.SET_BASE_USER_LIST,
      userList: formattedUsers,
    });
    dispatch({
      type: actionTypes.SET_USER_LIST_IS_LOADING,
      isLoading: false,
    });
    dispatch({
      type: actionTypes.FILTER_USER_LIST,
      authV3Enabled,
    });
  }, [rootUserListLoading, locationsOptions, rootUserListError, rootUserList]);

  const searchUsersData = useCallback((string) => {
    dispatch({
      type: actionTypes.FILTER_USER_LIST,
      authV3Enabled,
      searchString: string,
    });
  });

  const toggleDeactivateModal = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const toggleDrawer = useCallback(
    (title) => {
      setDrawerTitle(title);
      setShowDrawer(!showDrawer);
    },
    [showDrawer],
  );

  const { mutateAsync: userReactivateBulkQuery } = useMutation(({ userList }) =>
    userReactivateBulk(userList, authV3Enabled),
  );

  const reactivateUsers = useCallback(() => {
    const userCount = selectedUserList.length;
    const successMessage =
      userCount > 1
        ? `${userCount} users have been reactivated.`
        : `${userCount} user has been reactivated.`;

    dispatch({
      type: actionTypes.SET_TABLE_HEADER_STATE,
      tableHeaderState: false,
    });
    let userListPayload;
    if (authV3Enabled) {
      const userListData = selectedUserList.map((user) => {
        return { nextivaUuid: user.nextivaUuid };
      });
      userListPayload = { bulkUsers: userListData };
    } else {
      userListPayload = selectedUserList.map((user) => {
        return { login_id: user.email, profile_id: user.profiles[0].id }; // eslint-disable-line camelcase
      });
    }

    return userReactivateBulkQuery({
      userList: userListPayload,
    })
      .then(() => {
        updateRootUserList();
        queryClient.invalidateQueries(['getUsersBasicInfo']);
        const updatedBaseList = baseUserList.map((u) =>
          selectedUserList.some(
            (selectedUser) => selectedUser.email === u.email,
          )
            ? {
                ...u,
                active: true,
                status: true,
                statusDisplay: 'Active',
                updatedDate: new Date(),
                deactivatedDate: '',
                checked: false,
              }
            : u,
        );

        dispatch({
          type: actionTypes.SET_USER_LIST,
          userList: updatedBaseList.filter((u) => u.active === true),
        });

        dispatch({
          type: actionTypes.SET_BASE_USER_LIST,
          userList: updatedBaseList,
        });

        dispatch({
          type: actionTypes.SET_FILTER_STATUS,
          userFilterStatus: true,
        });

        if (userSearchString) {
          dispatch({
            type: actionTypes.FILTER_USER_LIST,
            authV3Enabled,
          });
        }

        setSnackbar({
          message: successMessage,
          severity: 'success',
        });
      })
      .catch(() => {
        setSnackbar({
          message: 'An error occurred while reactivating users.',
          severity: 'error',
        });
      });
  }, [selectedUserList]);

  const { mutateAsync: sendBulkEmailAsync } = useMutation((body) =>
    bulkEmail(body),
  );

  const sendBulkEmail = () => {
    return sendBulkEmailAsync({
      uuids: selectedUserList.map(
        (user) => user.nextivaUuid ?? user.nextiva_uuid,
      ),
    })
      .then(() => {
        dispatch({
          type: actionTypes.SET_USER_LIST,
          userList: userList.map((user) => ({
            ...user,
            checked: false,
          })),
        });

        dispatch({
          type: actionTypes.SET_BASE_USER_LIST,
          userList: baseUserList.map((user) => ({
            ...user,
            checked: false,
          })),
        });
        dispatch({
          type: actionTypes.SET_TABLE_HEADER_STATE,
          tableHeaderState: false,
        });
        setSnackbar({
          autoHideDuration: 2000,
          severity: 'default',
          message: 'Invite email sent',
        });
      })
      .catch(() => {
        setSnackbar({
          autoHideDuration: 2000,
          severity: 'error',
          message: 'Invite email was not able to be sent, please try again',
        });
      });
  };

  const {
    mutateAsync: passwordResetBulkQuery,
  } = useMutation(({ loginIds, authV3Enabled }) =>
    passwordResetBulk(loginIds, authV3Enabled),
  );

  const csvHeaderCols = [
    'Name',
    'Email',
    'Phone Number',
    'Extension',
    'Team',
    'Role',
    'Status',
    'Created Date',
    'Updated Date',
    'Deactivated Date',
    'Location',
    'Time Zone',
    'Voice-Enabled',
  ];

  const downloadUsersCSV = () => {
    const csvDownloadLink = document.createElement('a');

    const csvHeader = csvHeaderCols.join(',');
    const csvBody = userList
      .map(
        ({
          firstName,
          first_name,
          lastName,
          last_name,
          email,
          phoneNumber,
          extension,
          teamDisplay,
          roleDisplay,
          status,
          createdDate,
          updatedDate,
          deactivatedDate,
          locationDisplay,
          timeZone,
          profiles,
        }) =>
          [
            `${firstName ?? first_name} ${lastName ?? last_name}`,
            email,
            phoneNumber,
            extension,
            JSON.stringify(teamDisplay),
            roleDisplay,
            status ? 'Active' : 'Inactive',
            createdDate,
            updatedDate,
            deactivatedDate,
            locationDisplay,
            timeZone,
            profiles[0].nextOS?.primaryIdentifier ? 'Y' : 'N',
          ].join(','),
      )
      .join('\n');
    const csvData = `${csvHeader}\n${csvBody}`;
    const file = new Blob([csvData], { type: 'text/plain' });
    csvDownloadLink.href = URL.createObjectURL(file);
    csvDownloadLink.download = `users.csv`;
    csvDownloadLink.click();
  };

  const resetUserPasswords = useCallback(() => {
    const userCount = selectedUserList.length;
    const successMessage = `A password reset has been sent to ${userCount} user${
      userCount > 1 ? 's' : ''
    }`;

    return passwordResetBulkQuery({
      loginIds: authV3Enabled
        ? selectedUserList.map((user) => user.nextivaUuid)
        : selectedUserList.map((user) => user.email),
      authV3Enabled,
    })
      .then(() => {
        dispatch({
          type: actionTypes.SET_USER_LIST,
          userList: userList.map((user) => ({
            ...user,
            checked: false,
          })),
        });

        dispatch({
          type: actionTypes.SET_BASE_USER_LIST,
          userList: baseUserList.map((user) => ({
            ...user,
            checked: false,
          })),
        });

        dispatch({
          type: actionTypes.SET_TABLE_HEADER_STATE,
          tableHeaderState: false,
        });

        setSnackbar({
          autoHideDuration: 2000,
          severity: 'success',
          message: successMessage,
        });
      })
      .catch(() => {
        setSnackbar({
          autoHideDuration: 2000,
          severity: 'error',
          message: 'An error occurred while resetting passwords.',
        });
      });
  }, [selectedUserList]);

  /**
   * UserActiveDeactivate get passed into the Drawer
   * TitleComponent prop and shows the state of the user
   * @TODO: break this out in to its own component
   */
  const UserActiveDeactivate = () => {
    const { active: activeStatus, profiles } = editUser;
    const active = authV3Enabled
      ? profiles?.find(
          (profile) => profile.corporateAccountNumber === corpAcctNbr,
        )?.active
      : activeStatus;

    return (
      drawerTitle === EDIT_USER && (
        <div
          className={`user-state user-state__text${
            active ? '--active' : '--inactive'
          }`}
          data-testid={`PlatformAdminUI-UsersLink-ManageUsers-UserStateText${
            active ? '--active' : '--inactive'
          }`}
        >
          {active ? <SvgActive /> : <SvgInActive />}
          <div
            className="user-state__text"
            data-testid="PlatformAdminUI-UsersLink-ManageUsers-UserStateText"
          >
            {active ? 'Active' : 'Inactive'}
          </div>
        </div>
      )
    );
  };

  const onNotifyCreateUser = () => {
    dispatch({
      type: actionTypes.SET_NOTIFY_MODEL,
      notifyModal: {
        open: false,
        createUser: true,
      },
    });
  };

  const onAssignLicenses = () => {
    dispatch({
      type: actionTypes.SET_NOTIFY_MODEL,
      notifyModal: {
        open: false,
        scrollToBottom: true,
      },
    });
  };

  const onVoiceSetupModalLicenses = () => {
    dispatch({
      type: actionTypes.SET_VOICE_SETUP_MODEL,
      voiceSetupModel: {
        open: false,
        scrollToBottom: true,
      },
    });
    toggleDrawer(EDIT_USER);
  };

  const onVoiceSetupModalCancel = () => {
    dispatch({
      type: actionTypes.SET_VOICE_SETUP_MODEL,
      voiceSetupModel: { open: false },
    });
  };

  return (
    <Page title="Users">
      {isClusterIdInBWPatching && (isVoiceAccount || primaryIdentifier) && (
        <div className="banner" style={{ marginBottom: 32 }}>
          <Alert fullWidth severity="info">
            {VOICE_ADMIN_DISABLE_DUETO_BWPATCHING}
          </Alert>
        </div>
      )}
      <UserContext.Provider value={contextValue}>
        <div className="m-header-search-bar">
          <SearchBarWrapper
            id={screens.USERS_IMPORT}
            label="Current Users"
            placeholder="Search users"
            searchData={searchUsersData}
            actionName="Add user"
            importDropdownOptions={importDropdownOptions}
            onClick={() => toggleDrawer(CREATE_USER)}
            downloadCSV={downloadUsersCSV}
            data-testid="PlatformAdminUI-ManageUsers-SearchBar"
          />
          <TimedAlert setFireAlert={setFireAlert} />
        </div>
        {!userListIsLoading && (
          <React.Fragment>
            <TableHeader
              onAddTeam={() => toggleDrawer(ADD_TEAM)}
              onDeactivate={toggleDeactivateModal}
              onResetPassword={resetUserPasswords}
              onReactivate={reactivateUsers}
              sendBulkEmail={sendBulkEmail}
            />
            <div style={{ height: 'calc(100vh - 340px)' }}>
              <DispatchChimeContext.Provider value={chimeDispatch}>
                <UserList onEditUser={() => toggleDrawer(EDIT_USER)} />
                <ChimeContext.Provider value={chimeState}>
                  <ChimeDrawer />
                </ChimeContext.Provider>
              </DispatchChimeContext.Provider>
            </div>
          </React.Fragment>
        )}
        {userListIsLoading && <ShadowUserList />}
        {hasUserListError && <div>No users found.</div>}
        <Drawer
          title={getDrawerTitle(drawerTitle, editUser)}
          open={showDrawer}
          toggleDrawer={() => toggleDrawer(drawerTitle)}
          TitleComponent={UserActiveDeactivate}
        >
          <UserDrawerContent
            drawerTitle={drawerTitle}
            toggleDrawer={toggleDrawer}
          />
        </Drawer>
        {isModalOpen && (
          <DeactivateUsersModal toggleModal={toggleDeactivateModal} />
        )}
        {openCreateUserModel && (
          <CreateUserModal
            onCancel={onNotifyCreateUser}
            onConfirm={onAssignLicenses}
          />
        )}
        {openVoiceSetupModel && (
          <VoiceSetupModel
            onCancel={onVoiceSetupModalCancel}
            onConfirm={onVoiceSetupModalLicenses}
            userName={userName}
            pageName={pageNameVoiceSetupModel}
          />
        )}
      </UserContext.Provider>
    </Page>
  );
};

export default ManageUsers;
